/**
 * Camera reset plumbing for the lobby 3D canvas.
 *
 * `BoardCanvas3D` mounts a `<CameraResetListener>` that listens for the
 * global `CAMERA_RESET_EVENT` and animates the camera + OrbitControls
 * target back to the initial framing. Any UI surface (footer button,
 * hotkey handler, onboarding tour) can trigger that reset by calling
 * `resetLobbyCamera()` — decoupled from the Canvas so non-R3F code
 * can call it without reaching into Three internals.
 *
 * The default framing lives here too so the Canvas setup and the
 * listener agree on what "home" means — previously the pos/target were
 * duplicated as inline literals across the component.
 */

import * as THREE from 'three';

import { boardFootprint } from './tile-layout';
import type { GraphicsConfig } from '../settings/graphics-settings';

/** Global DOM event name. Dispatched on `window`. */
export const CAMERA_RESET_EVENT = 'takeover:camera:reset';

/** Reference grid dimension used when computing the default camera
 *  offset — the initial pose (0, 9.5, 15.5) was hand-tuned for this size,
 *  so larger / smaller boards scale relative to it. */
const REFERENCE_GRID_SIZE = 10;

/**
 * Multiplier applied to the base camera offset so the framing
 * matches the board footprint. A 16×16 board (gridSize=16) gets pulled
 * ~1.67× further back than the 10×10 reference so the whole plinth still
 * fits in frame.
 */
export function cameraScaleForGrid(gridSize?: number): number {
  if (!gridSize || gridSize <= 0) return 1;
  // `(N - 1)` matches `boardFootprint` — tile spacing cancels out so the
  // ratio depends only on inter-centre row count.
  const ref = REFERENCE_GRID_SIZE - 1;
  const actual = gridSize - 1;
  return Math.max(0.5, actual / ref);
}

/**
 * Nudges the orbit focus toward +X / +Z (bottom-right of the board in world
 * space) because `buildPlacements` ranks cheapest seats top-left and priciest
 * bottom-right — vacant slots skew to the back-left at load, which otherwise
 * dominates the first frame. Strength scales with board footprint; optional
 * `lodPolicy` tweaks how hard we pull (aggressive LOD needs a slightly stronger
 * bias so nearby tiles keep full avatar overlays in view).
 *
 * Mobile gets zero bias: viewports are narrow enough that any off-centre
 * framing pushes the top row behind the `MobileBoardRail`, so the home
 * pose should sit the board dead-centre even if that means showing more
 * vacant seats at first load.
 */
export function boardLookTargetBiasScale(
  lodPolicy: GraphicsConfig['lodPolicy'],
  twoD: boolean,
  isMobile = false,
): number {
  if (isMobile) return 0;
  if (twoD) return 1;
  switch (lodPolicy) {
    case 'aggressive':
      return 1.08;
    case 'balanced':
      return 1;
    case 'always-full':
      return 0.9;
    default:
      return 1;
  }
}

/**
 * Ramps the look-target bias down on small boards. The base bias
 * (`q = 0.2 × footprint × scale`) was tuned for the 10×10 reference and
 * larger, where `buildPlacements` puts vacant seats in the back-left
 * and the upper-left tiles dominate the first frame if the camera
 * doesn't lean toward +X / +Z. Below the reference size the same
 * proportional shift over-rotates: a 4×4 with all six seats held has
 * no upper-left vacant cluster to compensate for, but the bias still
 * shoves the entire board into the lobby's left-column HUD stack
 * (BoardListPanel + Yield/Holders), making most tiles unhoverable.
 *
 *   gridSize  →  sizeBias  ×  legacy q
 *   ────────     ────────     ────────
 *   ≤ 4              0.00         0.20  →   0
 *   5                0.17         0.20  →   0.033
 *   6                0.33         0.20  →   0.067
 *   8                0.67         0.20  →   0.133
 *   ≥ 10             1.00         0.20  →   0.200  (unchanged)
 *
 * Big boards (10×10, 16×16, 20×20) keep the existing framing exactly;
 * smaller boards centre cleanly inside the canvas free-zone.
 */
function smallBoardBiasScale(gridSize: number): number {
  return Math.max(0, Math.min(1, (gridSize - 4) / (REFERENCE_GRID_SIZE - 4)));
}

/** World-space point the camera should look at before applying height offset. */
export function defaultCameraTarget(
  gridSize?: number,
  lodPolicy: GraphicsConfig['lodPolicy'] = 'balanced',
  twoD = false,
  isMobile = false,
): THREE.Vector3 {
  const gs = gridSize && gridSize > 0 ? gridSize : REFERENCE_GRID_SIZE;
  const fp = boardFootprint(gs);
  const q =
    0.2 *
    smallBoardBiasScale(gs) *
    boardLookTargetBiasScale(lodPolicy, twoD, isMobile);
  return new THREE.Vector3(fp * q, 0, fp * q);
}

/**
 * Pulls the camera slightly closer on aggressive overlay LOD so more tiles
 * stay inside the “full” avatar band at the default pose.
 */
export function initialCameraDistanceMultiplier(
  lodPolicy: GraphicsConfig['lodPolicy'],
): number {
  switch (lodPolicy) {
    case 'aggressive':
      return 0.9;
    case 'balanced':
      return 0.96;
    case 'always-full':
    default:
      return 1;
  }
}

/** Initial camera position, matching the `camera={{ position: ... }}`
 *  prop on `<Canvas>`. When `gridSize` is supplied the offset scales
 *  proportionally so larger boards still fit in frame. New copy each
 *  call so callers can mutate freely. */
export function defaultCameraPosition(
  gridSize?: number,
  distanceMul = 1,
  lodPolicy: GraphicsConfig['lodPolicy'] = 'balanced',
  twoD = false,
  isMobile = false,
): THREE.Vector3 {
  const target = defaultCameraTarget(gridSize, lodPolicy, twoD, isMobile);
  const s = cameraScaleForGrid(gridSize);
  const ox = 0;
  const oy = 9.5 * s * distanceMul;
  const oz = 15.5 * s * distanceMul;
  return new THREE.Vector3(target.x + ox, target.y + oy, target.z + oz);
}

/** Initial pan (px) for Lite 2D — shift view toward the dense bottom-right.
 *  Mobile skips the bias entirely so the board loads centred inside the
 *  narrow viewport. */
export function initial2DBoardPanPixels(
  boardSizePx: number,
  lodPolicy: GraphicsConfig['lodPolicy'],
  isMobile = false,
): { x: number; y: number } {
  if (isMobile) return { x: 0, y: 0 };
  const s =
    lodPolicy === 'aggressive' ? 1.08 : lodPolicy === 'always-full' ? 0.9 : 1;
  return {
    x: -0.06 * boardSizePx * s,
    y: -0.12 * boardSizePx * s,
  };
}

/** Tween duration in seconds. ~450 ms matches the rest of the lobby
 *  UI's "snap but legible" animations (panel reset, skybox swap). */
export const CAMERA_RESET_DURATION_S = 0.45;

/**
 * Fire the global reset event. Safe to call from anywhere — if the
 * canvas isn't mounted (e.g. the user is in 2D mode) nothing happens.
 */
export function resetLobbyCamera() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CAMERA_RESET_EVENT));
}
